<template>
  <div class="aq-page">

    <!-- Top Bar -->
    <div class="topbar">
      <div class="topbar-left">
        <v-btn icon class="back-btn" @click="editQuote ? editQuote = null : $router.back()">
          <v-icon color="white" size="18">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="topbar-title">{{ editQuote ? editQuote.quoteNumber : 'Quoted' }}</div>
      </div>
      <div class="topbar-right">
        <v-btn v-if="!editQuote" icon class="icon-btn" @click="loadQuotes" :loading="loading" title="Refresh">
          <v-icon color="white" size="18">mdi-refresh</v-icon>
        </v-btn>
        <template v-if="editQuote">
          <v-btn v-if="editQuote.pdfUrl" class="btn-save btn-pdf" :loading="pdfDownloading" @click="downloadLatestPdf">
            <v-icon left size="14">mdi-download</v-icon>
            Download PDF
          </v-btn>
          <!-- PO Attach button — in Quoted view, not Edit Quote -->
          <input ref="poFileInput" type="file" accept=".pdf,.jpg,.jpeg,.png" style="display:none" @change="onPoFileSelected" />
          <v-btn class="btn-save btn-po" @click="$refs.poFileInput.click()" :loading="poUploading">
            <v-icon left size="14">mdi-paperclip</v-icon>
            {{ editQuote.poFile ? 'Replace PO' : 'Attach PO' }}
          </v-btn>
          <v-btn
            v-if="canEditQuote(editQuote)"
            class="btn-save btn-edit"
            @click="goToEditQuote(editQuote)">
            <v-icon left size="14">mdi-pencil-outline</v-icon>
            Edit Quote
          </v-btn>
          <v-btn
            v-if="isAdmin && editQuote"
            class="btn-save btn-admin"
            @click="showAdminRevert = true">
            <v-icon left size="14">mdi-shield-account-outline</v-icon>
            Admin Override
          </v-btn>
          <v-btn class="btn-save" :loading="updatingStatus" @click="saveQuoteChanges">
            <v-icon left size="14">mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </template>
      </div>
    </div>

    <!-- ── LIST VIEW ── -->
    <div v-if="!editQuote" class="page-content">

      <div class="section-label">Overview</div>
      <div class="stats-grid">
        <div class="stat-card" :class="filterStatus === '' ? 'stat-active' : ''"
          style="--sc:#1e2d3d" @click="filterStatus = ''">
          <div class="stat-num">{{ quotes.length }}</div>
          <div class="stat-lbl">All Quotes</div>
        </div>
        <div v-for="s in statsBar" :key="s.status"
          class="stat-card" :class="filterStatus === s.status ? 'stat-active' : ''"
          :style="`--sc:${s.color}`"
          @click="filterStatus = filterStatus === s.status ? '' : s.status">
          <div class="stat-num" :style="`color:${s.color}`">{{ s.count }}</div>
          <div class="stat-lbl">{{ s.label }}</div>
        </div>
      </div>

      <div class="section-label">Filter &amp; Search</div>
      <div class="filter-row">
        <div class="app-input" style="flex:1;max-width:500px">
          <v-icon size="16" color="#94a3b8">mdi-magnify</v-icon>
          <input v-model="search"
            placeholder="Search Quote Number / Customer / Reference…"
            class="app-input-field" />
        </div>
        <div class="filter-select">
          <v-icon size="14" color="#94a3b8">mdi-account-outline</v-icon>
          <select v-model="filterRep" class="native-select">
            <option value="">Filter by Rep</option>
            <option v-for="r in repList" :key="r" :value="r">{{ r }}</option>
          </select>
          <v-icon size="13" color="#94a3b8">mdi-chevron-down</v-icon>
        </div>
        <div class="filter-select">
          <v-icon size="14" color="#94a3b8">mdi-filter-outline</v-icon>
          <select v-model="filterStatus" class="native-select">
            <option value="">Filter by Status</option>
            <option v-for="s in statusOptions" :key="s.value" :value="s.value">{{ s.label }}</option>
          </select>
          <v-icon size="13" color="#94a3b8">mdi-chevron-down</v-icon>
        </div>
      </div>

      <div class="section-label">Quotes</div>

      <div v-if="loading" style="text-align:center;padding:48px">
        <v-progress-circular indeterminate color="#1e2d3d" />
      </div>

      <div v-else-if="!filteredQuotes.length" class="empty-state">
        <v-icon size="48" color="#cbd5e1">mdi-file-search-outline</v-icon>
        <div style="margin-top:10px">No quotes found</div>
      </div>

      <div v-else class="cards-grid">
        <div v-for="q in paginatedQuotes" :key="q.id" class="q-card">
          <div class="q-card-top">
            <span class="q-num">{{ q.quoteNumber }}</span>
            <div style="display:flex;align-items:center;gap:5px">
              <span v-if="q.currentRevision > 1" class="rev-badge">Rev {{ q.currentRevision }}</span>
              <span :class="['status-chip', 'status-' + q.status]">{{ statusLabel(q.status) }}</span>
            </div>
          </div>
          <div class="q-card-body">
            <div class="q-row"><span class="q-lbl">Customer:</span><span class="q-val">{{ q.customerName }}</span></div>
            <div class="q-row"><span class="q-lbl">Account:</span><span class="q-val">{{ q.accountCode }}</span></div>
            <div class="q-row"><span class="q-lbl">Reference:</span><span class="q-val">{{ q.customerReference || '—' }}</span></div>
            <div class="q-row"><span class="q-lbl">Rep:</span><span class="q-val">{{ q.createdBy }}</span></div>
            <div class="q-row"><span class="q-lbl">Valid Until:</span><span class="q-val">{{ q.validUntil }}</span></div>
            <div class="q-row"><span class="q-lbl">Total:</span><span class="q-val q-total">{{ fmt(q.totalExcl) }}</span></div>
          </div>
          <div class="q-card-foot">
            <div style="display:flex;align-items:center;gap:6px">
              <span class="items-badge">{{ q.lineCount || 0 }} items</span>
              <span v-if="q.poFile" class="po-badge">
                <v-icon size="10" color="#4338ca">mdi-paperclip</v-icon> PO
              </span>
            </div>
            <button class="manage-btn" @click="openEdit(q)">
              <v-icon size="12" color="white" style="margin-right:4px">mdi-cog-outline</v-icon>
              Manage Quote
            </button>
          </div>
        </div>
      </div>

      <div v-if="filteredQuotes.length > pageSize" class="pagination">
        <v-btn icon small :disabled="page === 1" @click="page--"><v-icon small>mdi-chevron-left</v-icon></v-btn>
        <span class="page-info">{{ page }} / {{ totalPages }}</span>
        <v-btn icon small :disabled="page === totalPages" @click="page++"><v-icon small>mdi-chevron-right</v-icon></v-btn>
      </div>

    </div>

    <!-- ── EDIT / APPROVE VIEW ── -->
    <div v-else-if="editQuote" class="page-content">

      <!-- Breadcrumb -->
      <div class="edit-subhdr">
        <button class="back-link" @click="editQuote = null">
          <v-icon size="14" color="#64748b">mdi-arrow-left</v-icon>
          Back to Quoted
        </button>
        <span style="color:#cbd5e1;font-size:12px">/</span>
        <span style="font-size:12px;color:#64748b">{{ editQuote.customerName }}</span>
        <span style="color:#cbd5e1;font-size:12px">/</span>
        <span :class="['status-chip', 'status-' + editQuote.status]">{{ statusLabel(editQuote.status) }}</span>
        <span v-if="editQuote.currentRevision > 1" class="rev-badge" style="margin-left:4px">Rev {{ editQuote.currentRevision }}</span>
      </div>

      <!-- PO attachment display -->
      <div v-if="editQuote.poFile" class="po-attached-bar">
        <div class="po-attached-left">
          <v-icon size="16" color="#4338ca">mdi-file-document-outline</v-icon>
          <span class="po-filename">{{ editQuote.poFile.name }}</span>
          <span class="po-meta">{{ editQuote.poFile.uploadedAt }} · {{ editQuote.poFile.uploadedBy }}</span>
        </div>
        <div class="po-attached-right">
          <a :href="editQuote.poFile.url" target="_blank" class="po-link">View</a>
          <a :href="editQuote.poFile.url" :download="editQuote.poFile.name" class="po-link">Download</a>
        </div>
      </div>

      <!-- Revision history -->
      <div v-if="editQuote.revisions && editQuote.revisions.length > 1" class="revision-bar">
        <v-icon size="13" color="#64748b">mdi-history</v-icon>
        <span class="rev-label">Revision History:</span>
        <span v-for="(r, i) in [...editQuote.revisions].reverse().slice(0,5)" :key="i" class="rev-chip">
          Rev {{ r.revision }} · {{ r.modifiedBy }} · {{ formatDate(r.modifiedAt) }}
        </span>
      </div>

      <!-- Summary cards -->
      <div class="section-label">Summary</div>
      <div class="sum-grid">
        <div class="sum-card">
          <div class="sum-num">{{ fmt(editQuote.totalExcl) }}</div>
          <div class="sum-lbl">Quote Total</div>
        </div>
        <div class="sum-card">
          <div class="sum-num" style="color:#059669">{{ fmt(approvedTotal) }}</div>
          <div class="sum-lbl">Approved Total</div>
        </div>
        <div class="sum-card">
          <div class="sum-num" style="color:#059669">{{ approvedCount }} / {{ editQuote.lineItems.length }}</div>
          <div class="sum-lbl">Items Approved</div>
        </div>
        <div class="sum-card">
          <div class="sum-num" :style="pendingCount === 0 ? 'color:#059669' : 'color:#f59e0b'">
            {{ pendingCount === 0 ? 'All resolved' : pendingCount + ' pending' }}
          </div>
          <div class="sum-lbl">Approval Status</div>
        </div>
      </div>

      <!-- PO bar -->
      <div class="po-bar">
        <div class="po-label">
          <v-icon size="14" color="#6366f1">mdi-tag-outline</v-icon>
          Customer PO / Order #
        </div>
        <input v-model="editQuote.customerPO" placeholder="Enter customer PO or order number…" class="po-input" />
      </div>

      <!-- Line items with approval -->
      <div class="section-label">Line Items — Approve or Decline</div>
      <div class="tbl-card">
        <div class="tbl-head">
          <div class="th" style="width:28px">#</div>
          <div class="th" style="flex:1">Product / Description</div>
          <div class="th" style="width:52px;text-align:center">Qty</div>
          <div class="th" style="width:100px;text-align:right">Line Total</div>
          <div class="th" style="width:80px;text-align:center">Qty Order</div>
          <div class="th" style="width:76px;text-align:center">Action</div>
        </div>

        <div v-for="(item, idx) in editQuote.lineItems" :key="idx"
          :class="['tbl-row',
            idx % 2 === 1 ? 'tbl-row-alt' : '',
            item.approved === true ? 'tbl-row-approved' : '',
            item.approved === false ? 'tbl-row-declined' : '']">

          <div style="width:28px;color:#cbd5e1;font-size:11px;padding-top:4px">{{ idx + 1 }}</div>

          <div style="flex:1;min-width:0">
            <div style="display:flex;align-items:center;gap:5px;margin-bottom:3px;flex-wrap:wrap">
              <span v-if="item.code" class="code-pill">{{ item.code }}</span>
              <span v-if="item.type === 'freetext'" class="free-pill">unlisted</span>
              <span v-if="item.isBom" class="bom-pill">BOM</span>
            </div>
            <div style="font-size:13px;color:#334155;line-height:1.3">{{ item.description }}</div>
            <div v-if="item.note" style="font-size:11px;color:#94a3b8;font-style:italic;margin-top:2px">{{ item.note }}</div>
            <div v-if="item.discount > 0" style="font-size:10px;color:#94a3b8;margin-top:2px">{{ item.discount }}% discount applied</div>
          </div>

          <div style="width:52px;text-align:center;font-size:12px;color:#64748b;padding-top:4px">×{{ item.qty }}</div>
          <div style="width:100px;text-align:right;font-size:13px;font-weight:700;color:#1e293b;padding-top:4px">{{ fmt(item.lineTotal) }}</div>

          <div style="width:80px;text-align:center;flex-shrink:0">
            <input
              v-if="item.approved === true"
              :value="item.qtyOrdered || item.qty"
              type="number" :min="1" :max="item.qty"
              class="qty-order-input"
              @change="item.qtyOrdered = Math.min(Math.max(1, Number($event.target.value)), item.qty)"
            />
            <span v-else style="font-size:11px;color:#cbd5e1">—</span>
          </div>

          <div class="apr-btns">
            <button :class="['apr-btn', item.approved === true ? 'apr-ok' : '']"
              @click="toggleApproval(item, true)" title="Approve">
              <v-icon size="16" :color="item.approved === true ? '#16a34a' : '#94a3b8'">mdi-check</v-icon>
            </button>
            <button :class="['apr-btn', item.approved === false ? 'apr-no' : '']"
              @click="toggleApproval(item, false)" title="Decline">
              <v-icon size="16" :color="item.approved === false ? '#dc2626' : '#94a3b8'">mdi-close</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Approved total bar -->
      <div v-if="approvedCount > 0" class="approved-bar">
        <div class="approved-bar-label">
          <v-icon size="14" color="#15803d">mdi-check-circle</v-icon>
          Approved items total
        </div>
        <div class="approved-bar-val">{{ fmt(approvedTotal) }}</div>
      </div>

      <!-- Status + Note + Meta/History -->
      <v-row>
        <v-col cols="12" md="6">
          <div class="section-label">Update Status</div>
          <div class="card">
            <div class="status-grid">
              <div v-for="s in allowedStatusOptions" :key="s.value"
                :class="['status-opt', editPendingStatus === s.value ? 'status-opt-sel' : '', s.disabled ? 'status-opt-disabled' : '']"
                :style="editPendingStatus === s.value ? `border-color:${s.color};background:${s.color}14` : ''"
                @click="!s.disabled && (editPendingStatus = s.value)">
                <div class="status-dot" :style="`background:${s.disabled ? '#e2e8f0' : s.color}`"></div>
                <span :style="s.disabled ? 'color:#cbd5e1' : editPendingStatus === s.value ? `color:${s.color};font-weight:700` : ''">{{ s.label }}</span>
                <v-icon v-if="s.disabled" size="10" color="#cbd5e1" style="margin-left:auto">mdi-lock</v-icon>
              </div>
            </div>
            <div v-if="!isAdmin" style="font-size:10px;color:#94a3b8;margin-top:8px;display:flex;align-items:center;gap:4px">
              <v-icon size="11" color="#cbd5e1">mdi-information-outline</v-icon>
              Status can only move forward. Contact an admin to revert.
            </div>
          </div>

          <div class="section-label" style="margin-top:4px">Note</div>
          <div class="card">
            <div class="field-label" style="margin-bottom:6px">Note / Reason</div>
            <textarea v-model="statusNote"
              placeholder="Reason for status change, approval reference, customer instruction…"
              class="app-textarea" rows="4" />
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="section-label">Quote Info</div>
          <div class="card">
            <div class="meta-row"><span class="meta-k">Created by</span><span class="meta-v">{{ editQuote.createdBy }}</span></div>
            <div class="meta-row"><span class="meta-k">Date</span><span class="meta-v">{{ formatDate(editQuote.createdAt) }}</span></div>
            <div class="meta-row"><span class="meta-k">Valid until</span><span class="meta-v">{{ editQuote.validUntil }}</span></div>
            <div v-if="editQuote.currentRevision > 1" class="meta-row">
              <span class="meta-k">Revision</span>
              <span class="meta-v" style="color:#d97706;font-weight:800">Rev {{ editQuote.currentRevision }}</span>
            </div>
            <div v-if="editQuote.customerReference" class="meta-row">
              <span class="meta-k">Customer ref</span><span class="meta-v">{{ editQuote.customerReference }}</span>
            </div>
            <div v-if="editQuote.customerNotes" class="meta-row">
              <span class="meta-k">Notes</span><span class="meta-v">{{ editQuote.customerNotes }}</span>
            </div>
            <div v-if="editQuote.customerPO" class="meta-row">
              <span class="meta-k">Customer PO</span><span class="meta-v" style="color:#4338ca;font-weight:700">{{ editQuote.customerPO }}</span>
            </div>
            <div class="meta-row">
              <span class="meta-k">Total excl. VAT</span>
              <span class="meta-v" style="font-weight:800;color:#1e293b">{{ fmt(editQuote.totalExcl) }}</span>
            </div>
          </div>

          <div class="section-label" style="margin-top:4px">Status History</div>
          <div class="card">
            <div v-for="(h, idx) in [...(editQuote.statusHistory || [])].reverse()" :key="idx" class="hist-item">
              <div class="hist-dot" :style="`background:${statusColor(h.status)}`"></div>
              <div>
                <div style="font-size:12px;color:#334155">
                  <span :style="`color:${statusColor(h.status)};font-weight:700`">{{ statusLabel(h.status) }}</span>
                  <span style="color:#94a3b8"> — {{ h.changedBy }}</span>
                </div>
                <div style="font-size:10px;color:#94a3b8;margin-top:1px">{{ h.timestamp ? formatDate(h.timestamp) : '' }}</div>
                <div v-if="h.note" style="font-size:10px;color:#64748b;font-style:italic;margin-top:2px">{{ h.note }}</div>
              </div>
            </div>
            <div v-if="!(editQuote.statusHistory && editQuote.statusHistory.length)"
              style="font-size:12px;color:#94a3b8;text-align:center;padding:8px">No history yet</div>
          </div>
        </v-col>
      </v-row>

    </div>

    <!-- Admin override dialog -->
    <v-dialog v-model="showAdminRevert" max-width="420">
      <div class="admin-dialog">
        <div class="admin-dialog-head">
          <v-icon color="white" size="18">mdi-shield-account-outline</v-icon>
          Admin Status Override
        </div>
        <div class="admin-dialog-body">
          <div class="field-label" style="margin-bottom:8px">Set status to any value</div>
          <div class="status-grid">
            <div v-for="s in statusOptions" :key="s.value"
              :class="['status-opt', adminOverrideStatus === s.value ? 'status-opt-sel' : '']"
              :style="adminOverrideStatus === s.value ? `border-color:${s.color};background:${s.color}14` : ''"
              @click="adminOverrideStatus = s.value">
              <div class="status-dot" :style="`background:${s.color}`"></div>
              <span :style="adminOverrideStatus === s.value ? `color:${s.color};font-weight:700` : ''">{{ s.label }}</span>
            </div>
          </div>
          <div class="field-label" style="margin-top:14px;margin-bottom:6px">Reason (required)</div>
          <textarea v-model="adminOverrideNote" placeholder="Reason for override…" class="app-textarea" rows="3" />
        </div>
        <div class="admin-dialog-foot">
          <v-btn text @click="showAdminRevert = false" style="color:#64748b;text-transform:none">Cancel</v-btn>
          <v-btn
            :disabled="!adminOverrideStatus || !adminOverrideNote"
            class="btn-save"
            style="background:#1e2d3d!important;color:#fff!important"
            @click="applyAdminOverride">
            Apply Override
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" top right :timeout="3500">
      {{ snackMsg }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false" small>Close</v-btn>
      </template>
    </v-snackbar>

  </div>
</template>

<script>
import { db, auth, storage } from '@/firebaseConfig'
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, orderBy, query, serverTimestamp
} from 'firebase/firestore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { generateQuotePdf, downloadPdfBlob } from '@/services/quotePdf'

const STATUS_ORDER = ['sent', 'viewed', 'order_placed', 'completed', 'rejected', 'expired']

const STATUS_MAP = {
  sent:         { label: 'Sent',         color: '#3b82f6' },
  viewed:       { label: 'Viewed',       color: '#8b5cf6' },
  order_placed: { label: 'Order Placed', color: '#58eebc' },
  completed:    { label: 'Completed',    color: '#23d747' },
  rejected:     { label: 'Rejected',     color: '#ef4444' },
  expired:      { label: 'Expired',      color: '#94a3b8' },
}

export default {
  name: 'AllQuoted',

  data: () => ({
    currentUser: '', currentUserUid: '', isAdmin: false,
    quotes: [], loading: false,
    search: '', filterStatus: '', filterRep: '',
    page: 1, pageSize: 12,
    editQuote: null, editPendingStatus: '', statusNote: '', updatingStatus: false,
    pdfDownloading: false,
    poUploading: false,
    showAdminRevert: false,
    adminOverrideStatus: '',
    adminOverrideNote: '',
    snackbar: false, snackMsg: '', snackColor: 'success',
  }),

  computed: {
    statusOptions() {
      return Object.entries(STATUS_MAP).map(([value, m]) => ({ value, ...m }))
    },
    allowedStatusOptions() {
      const currentIdx = STATUS_ORDER.indexOf(this.editQuote?.status)
      return this.statusOptions.map(s => ({
        ...s,
        disabled: !this.isAdmin && STATUS_ORDER.indexOf(s.value) < currentIdx,
      }))
    },
    repList() {
      return [...new Set(this.quotes.map(q => q.createdBy).filter(Boolean))]
    },
    statsBar() {
      return Object.entries(STATUS_MAP).map(([status, m]) => ({
        status, label: m.label, color: m.color,
        count: this.quotes.filter(q => q.status === status).length,
      })).filter(s => s.count > 0)
    },
    filteredQuotes() {
      let q = this.quotes
      if (this.filterStatus) q = q.filter(x => x.status === this.filterStatus)
      if (this.filterRep)    q = q.filter(x => x.createdBy === this.filterRep)
      if (this.search) {
        const s = this.search.toLowerCase()
        q = q.filter(x =>
          x.quoteNumber?.toLowerCase().includes(s) ||
          x.customerName?.toLowerCase().includes(s) ||
          x.accountCode?.toLowerCase().includes(s) ||
          x.customerReference?.toLowerCase().includes(s)
        )
      }
      return q
    },
    totalPages()      { return Math.ceil(this.filteredQuotes.length / this.pageSize) || 1 },
    paginatedQuotes() {
      const s = (this.page - 1) * this.pageSize
      return this.filteredQuotes.slice(s, s + this.pageSize)
    },
    approvedCount() { return (this.editQuote?.lineItems || []).filter(i => i.approved === true).length },
    pendingCount()  { return (this.editQuote?.lineItems || []).filter(i => i.approved === null || i.approved === undefined).length },
    approvedTotal() {
      return (this.editQuote?.lineItems || [])
        .filter(i => i.approved === true)
        .reduce((s, i) => s + (i.lineTotal || 0), 0)
    },
  },

  async created() {
    await this.loadUser()
    await this.loadQuotes()
  },

  methods: {
    async loadUser() {
      const u = auth.currentUser; if (!u) return
      this.currentUserUid = u.uid
      try {
        const d = await getDoc(doc(db, 'users', u.uid))
        if (d.exists()) {
          const data = d.data()
          this.currentUser = data.displayName || u.email
          this.isAdmin = data.role === 'admin' || data.isAdmin === true
        } else {
          this.currentUser = u.email
        }
      } catch { this.currentUser = u.email || '' }
    },

    async loadQuotes() {
      this.loading = true
      try {
        const s = await getDocs(query(collection(db, 'quotes'), orderBy('createdAt', 'desc')))
        this.quotes = s.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) { console.error('loadQuotes', e) }
      this.loading = false
    },

    openEdit(quote) {
      this.editQuote = JSON.parse(JSON.stringify(quote))
      this.editPendingStatus = quote.status
      this.statusNote = ''
      this.adminOverrideStatus = ''
      this.adminOverrideNote   = ''
      window.scrollTo(0, 0)
    },

    goToEditQuote(quote) {
      this.$router.push({ name: 'EditQuotation', params: { id: quote.id } })
    },

    canEditQuote(quote) {
      if (this.isAdmin) return true
      return ['draft', 'sent'].includes(quote.status)
    },

    toggleApproval(item, val) {
      item.approved = item.approved === val ? null : val
      if (item.approved === true) this.$set(item, 'qtyOrdered', item.qty)
    },

    async onPoFileSelected(event) {
      const file = event.target.files[0]
      if (!file || !this.editQuote) return
      this.poUploading = true
      try {
        const path    = `purchase-orders/${this.editQuote.quoteNumber}/${Date.now()}_${file.name}`
        const fileRef = storageRef(storage, path)
        await uploadBytes(fileRef, file)
        const url = await getDownloadURL(fileRef)
        const poFile = {
          name:       file.name,
          url,
          path,
          uploadedBy: this.currentUser,
          uploadedAt: new Date().toISOString().slice(0, 10),
        }
        await updateDoc(doc(db, 'quotes', this.editQuote.id), { poFile })
        this.$set(this.editQuote, 'poFile', poFile)
        // Update local list too
        const idx = this.quotes.findIndex(q => q.id === this.editQuote.id)
        if (idx > -1) this.$set(this.quotes[idx], 'poFile', poFile)
        this.toast('PO attached successfully', 'success')
      } catch (e) {
        console.error('PO upload failed', e)
        this.toast('Failed to upload PO', 'error')
      } finally {
        this.poUploading = false
        this.$refs.poFileInput.value = ''
      }
    },

    async downloadLatestPdf() {
      if (!this.editQuote?.id) return
      this.pdfDownloading = true
      try {
        const snap = await getDoc(doc(db, 'quotes', this.editQuote.id))
        if (!snap.exists()) { this.toast('Quote not found', 'error'); return }
        const liveData = snap.data()
        const blob = await generateQuotePdf(liveData)
        downloadPdfBlob(blob, `${liveData.quoteNumber}.pdf`)
      } catch (e) {
        console.error('downloadLatestPdf', e)
        this.toast('Failed to generate PDF', 'error')
      }
      this.pdfDownloading = false
    },

    async saveQuoteChanges() {
      if (!this.editQuote) return
      if (!this.isAdmin) {
        const currentIdx = STATUS_ORDER.indexOf(this.editQuote.status)
        const newIdx     = STATUS_ORDER.indexOf(this.editPendingStatus)
        if (newIdx < currentIdx) {
          this.toast('Status cannot be reversed. Contact an admin.', 'error')
          return
        }
      }
      this.updatingStatus = true
      try {
        const now           = serverTimestamp()
        const statusChanged = this.editPendingStatus !== this.editQuote.status
        const historyEntry  = {
          status:       this.editPendingStatus,
          changedBy:    this.currentUser,
          changedByUid: this.currentUserUid,
          timestamp:    new Date().toISOString(),
          note:         this.statusNote || '',
        }
        const updateData = {
          lineItems:  this.editQuote.lineItems,
          customerPO: this.editQuote.customerPO || '',
          status:     this.editPendingStatus,
          updatedAt:  now,
        }
        if (statusChanged) {
          updateData.statusHistory = [...(this.editQuote.statusHistory || []), historyEntry]
        }
        await updateDoc(doc(db, 'quotes', this.editQuote.id), updateData)
        await addDoc(collection(db, 'auditLog'), {
          entityType:     'quote',
          entityId:       this.editQuote.id,
          quoteNumber:    this.editQuote.quoteNumber,
          action:         statusChanged ? 'STATUS_CHANGED' : 'ITEMS_UPDATED',
          previousStatus: this.editQuote.status,
          newStatus:      this.editPendingStatus,
          approvedItems:  this.approvedCount,
          performedBy:    this.currentUser,
          performedByUid: this.currentUserUid,
          notes:          this.statusNote || '',
          timestamp:      now,
        })
        const idx = this.quotes.findIndex(q => q.id === this.editQuote.id)
        if (idx > -1) {
          const updated = { ...this.quotes[idx], ...updateData, status: this.editPendingStatus }
          if (statusChanged) updated.statusHistory = [...(this.quotes[idx].statusHistory || []), historyEntry]
          this.$set(this.quotes, idx, updated)
        }
        this.toast('Quote updated successfully', 'success')
        this.editQuote = null
      } catch (e) {
        console.error(e)
        this.toast('Failed to update — check console', 'error')
      }
      this.updatingStatus = false
    },

    async applyAdminOverride() {
      if (!this.adminOverrideStatus || !this.adminOverrideNote) return
      this.editPendingStatus = this.adminOverrideStatus
      this.statusNote        = `[Admin Override] ${this.adminOverrideNote}`
      this.showAdminRevert   = false
      await this.saveQuoteChanges()
    },

    statusLabel(s) { return STATUS_MAP[s]?.label || s },
    statusColor(s) { return STATUS_MAP[s]?.color || '#94a3b8' },

    fmt(v) {
      return 'R ' + Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    formatDate(ts) {
      if (!ts) return '—'
      try {
        const d = ts.toDate ? ts.toDate() : new Date(ts)
        return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
      } catch { return '—' }
    },

    toast(msg, color = 'success') {
      this.snackMsg = msg; this.snackColor = color; this.snackbar = true
    },
  },
}
</script>

<style scoped>
.aq-page { background: #f1f5f9; min-height: 100vh; font-family: 'Inter', sans-serif; }

/* Topbar */
.topbar { background: #1e2d3d; padding: 14px 24px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.topbar-left { display: flex; align-items: center; gap: 12px; }
.back-btn { background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2) !important; border-radius: 50% !important; width: 34px !important; height: 34px !important; transition: background .15s !important; }
.back-btn:hover { background: rgba(255,255,255,0.2) !important; }
.topbar-title { color: #fff; font-size: 18px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
.topbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.icon-btn { background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.18) !important; border-radius: 8px !important; }
.btn-save { border-radius: 8px !important; font-size: 12px !important; font-weight: 700 !important; text-transform: none !important; letter-spacing: 0 !important; height: 36px !important; padding: 0 16px !important; }
.btn-pdf   { background: rgba(255,255,255,0.12) !important; color: #fff !important; border: 1px solid rgba(255,255,255,0.25) !important; }
.btn-pdf:hover { background: rgba(255,255,255,0.2) !important; }
.btn-po    { background: rgba(255,255,255,0.12) !important; color: #fff !important; border: 1px solid rgba(255,255,255,0.25) !important; }
.btn-po:hover { background: rgba(255,255,255,0.2) !important; }
.btn-edit  { background: #dbeafe !important; color: #1d4ed8 !important; }
.btn-edit:hover { background: #bfdbfe !important; }
.btn-admin { background: #fef3c7 !important; color: #92400e !important; }
.btn-admin:hover { background: #fde68a !important; }
.btn-save:not(.btn-pdf):not(.btn-po):not(.btn-edit):not(.btn-admin) { background: #fff !important; color: #1e2d3d !important; }
.btn-save:not(.btn-pdf):not(.btn-po):not(.btn-edit):not(.btn-admin):hover { background: #dcfce7 !important; color: #15803d !important; }

/* Content */
.page-content { padding: 20px 24px 48px; max-width: 1400px; margin: 0 auto; }
.section-label { font-size: 11px; font-weight: 800; letter-spacing: .1em; text-transform: uppercase; color: #64748b; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
.section-label::after { content: ''; flex: 1; height: 1px; background: #e2e8f0; }
.field-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #64748b; }

/* PO attached bar */
.po-attached-bar { background: #eef2ff; border: 1.5px solid #c7d2fe; border-radius: 10px; padding: 10px 16px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; flex-wrap: wrap; gap: 8px; }
.po-attached-left { display: flex; align-items: center; gap: 8px; }
.po-filename { font-size: 12px; font-weight: 700; color: #4338ca; }
.po-meta { font-size: 11px; color: #94a3b8; }
.po-attached-right { display: flex; gap: 12px; }
.po-link { font-size: 12px; font-weight: 600; color: #4338ca; text-decoration: none; }
.po-link:hover { text-decoration: underline; }

/* Revision bar */
.revision-bar { background: #fefce8; border: 1px solid #fde68a; border-radius: 8px; padding: 8px 16px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rev-label { font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: #92400e; }
.rev-chip { font-size: 11px; color: #78350f; background: #fef3c7; border: 1px solid #fde68a; border-radius: 5px; padding: 2px 8px; }
.rev-badge { font-size: 10px; color: #92400e; background: #fef3c7; border: 1px solid #fde68a; border-radius: 5px; padding: 2px 7px; font-weight: 700; }

/* Stats */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 10px; margin-bottom: 18px; }
.stat-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; cursor: pointer; transition: all .15s; user-select: none; }
.stat-card:hover { border-color: #94a3b8; }
.stat-card.stat-active { border-color: var(--sc); border-width: 2px; }
.stat-num { font-size: 20px; font-weight: 800; color: #1e293b; }
.stat-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; margin-top: 2px; }

/* Filter */
.filter-row { display: flex; gap: 10px; margin-bottom: 18px; flex-wrap: wrap; align-items: center; }
.app-input { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; display: flex; align-items: center; gap: 7px; transition: border-color .15s; }
.app-input:hover { border-color: #94a3b8; }
.app-input-field { background: none; border: none; outline: none; font-size: 13px; color: #1e293b; flex: 1; font-family: inherit; }
.app-input-field::placeholder { color: #94a3b8; }
.filter-select { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; display: flex; align-items: center; gap: 6px; min-width: 160px; }
.native-select { background: none; border: none; outline: none; font-size: 12px; color: #64748b; flex: 1; font-family: inherit; cursor: pointer; }

/* Cards grid */
.cards-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; margin-bottom: 18px; align-items: stretch; }
.q-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; transition: all .15s; display: flex; flex-direction: column; height: 100%; }
.q-card:hover { border-color: #94a3b8; transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0,0,0,0.07); }
.q-card-top { background: #1e2d3d; padding: 10px 14px; display: flex; align-items: center; justify-content: space-between; }
.q-num { background: rgba(255,255,255,0.15); color: #fff; font-family: monospace; font-size: 11px; font-weight: 700; padding: 3px 8px; border-radius: 5px; }
.q-card-body { padding: 14px; flex: 1; }
.q-row { display: flex; margin-bottom: 7px; font-size: 12px; align-items: flex-start; }
.q-row:last-child { margin-bottom: 0; }
.q-lbl { font-weight: 700; color: #64748b; text-transform: uppercase; font-size: 10px; letter-spacing: .06em; min-width: 90px; padding-top: 1px; }
.q-val { color: #1e293b; font-weight: 500; flex: 1; }
.q-total { font-weight: 800 !important; color: #1e2d3d !important; }
.q-card-foot { padding: 10px 14px; border-top: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; }
.items-badge { font-size: 10px; color: #64748b; background: #f1f5f9; border-radius: 5px; padding: 3px 8px; font-weight: 600; }
.po-badge { font-size: 10px; color: #4338ca; background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 5px; padding: 2px 6px; display: inline-flex; align-items: center; gap: 2px; font-weight: 600; }
.manage-btn { background: #1e2d3d; color: #fff; border: none; border-radius: 7px; padding: 8px 14px; font-size: 11px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; text-transform: uppercase; letter-spacing: .04em; transition: background .15s; }
.manage-btn:hover { background: #2d3f52; }

/* Status chips */
.status-chip { font-size: 10px; padding: 3px 9px; border-radius: 20px; font-weight: 700; display: inline-block; text-transform: uppercase; letter-spacing: .04em; }
.status-sent         { background: #dbeafe; color: #1d4ed8; border: 1px solid #bfdbfe; }
.status-viewed       { background: #ede9fe; color: #6d28d9; border: 1px solid #ddd6fe; }
.status-order_placed { background: #d1fae5; color: #065f46; border: 1px solid #a7f3d0; }
.status-completed    { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
.status-rejected     { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
.status-expired      { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }

/* Edit subheader */
.edit-subhdr { display: flex; align-items: center; gap: 8px; margin-bottom: 18px; flex-wrap: wrap; }
.back-link { background: none; border: none; cursor: pointer; font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 4px; font-family: inherit; transition: color .15s; }
.back-link:hover { color: #1e293b; }

/* Summary */
.sum-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; }
.sum-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px; }
.sum-num { font-size: 18px; font-weight: 800; color: #1e293b; }
.sum-lbl { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #94a3b8; margin-top: 2px; }

/* PO / customer PO bar */
.po-bar { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.po-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #64748b; white-space: nowrap; display: flex; align-items: center; gap: 5px; }
.po-input { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; color: #1e293b; font-size: 13px; flex: 1; outline: none; font-family: inherit; transition: border-color .15s; }
.po-input:focus { border-color: #6366f1; }
.po-input::placeholder { color: #cbd5e1; }

/* Table */
.card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 18px; margin-bottom: 14px; }
.tbl-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; margin-bottom: 14px; }
.tbl-head { display: flex; gap: 8px; padding: 9px 16px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; align-items: center; }
.th { font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #94a3b8; }
.tbl-row { display: flex; gap: 8px; padding: 13px 16px; border-bottom: 1px solid #f8fafc; align-items: center; transition: background .1s; }
.tbl-row:last-child { border-bottom: none; }
.tbl-row-alt { background: #fafafa; }
.tbl-row:hover { background: #f8fafc; }
.tbl-row-approved { background: #f0fdf4 !important; border-left: 3px solid #22c55e; }
.tbl-row-declined { background: #fef2f2 !important; border-left: 3px solid #fca5a5; opacity: .8; }
.apr-btns { display: flex; gap: 4px; justify-content: center; width: 76px; flex-shrink: 0; }
.apr-btn { width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #e2e8f0; background: #f8fafc; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.apr-btn:hover { background: #f1f5f9; border-color: #94a3b8; }
.apr-ok { background: #dcfce7 !important; border-color: #22c55e !important; }
.apr-no { background: #fee2e2 !important; border-color: #f87171 !important; }
.qty-order-input { width: 58px; text-align: center; background: #f0fdf4; border: 1.5px solid #22c55e; border-radius: 6px; padding: 4px 6px; font-size: 12px; font-weight: 700; color: #15803d; outline: none; font-family: inherit; }
.qty-order-input:focus { border-color: #16a34a; }

/* Approved bar */
.approved-bar { background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 10px; padding: 11px 16px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.approved-bar-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #15803d; display: flex; align-items: center; gap: 5px; }
.approved-bar-val { font-size: 15px; font-weight: 800; color: #15803d; }

/* Status grid */
.status-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 7px; }
.status-opt { padding: 9px 10px; border-radius: 9px; border: 1.5px solid #e2e8f0; background: #f8fafc; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #64748b; transition: all .15s; }
.status-opt:hover:not(.status-opt-disabled) { border-color: #94a3b8; background: #f1f5f9; }
.status-opt-sel { font-weight: 700; }
.status-opt-disabled { cursor: not-allowed; opacity: .5; }
.status-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

/* Textarea */
.app-textarea { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 9px 12px; font-size: 13px; color: #1e293b; width: 100%; font-family: inherit; outline: none; resize: none; transition: border-color .15s; }
.app-textarea:focus { border-color: #6366f1; }
.app-textarea::placeholder { color: #94a3b8; }

/* Meta / History */
.meta-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f1f5f9; font-size: 12px; }
.meta-row:last-child { border-bottom: none; }
.meta-k { color: #94a3b8; font-weight: 500; }
.meta-v { color: #334155; font-weight: 600; text-align: right; max-width: 60%; }
.hist-item { display: flex; gap: 9px; padding: 7px 0; border-bottom: 1px solid #f1f5f9; }
.hist-item:last-child { border-bottom: none; }
.hist-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }

/* Pills */
.code-pill { font-family: monospace; font-size: 10px; color: #4338ca; background: #eef2ff; padding: 2px 6px; border-radius: 4px; }
.free-pill { font-size: 10px; color: #b45309; background: #fffbeb; padding: 2px 6px; border-radius: 4px; }
.bom-pill  { font-size: 10px; color: #7c3aed; background: #ede9fe; padding: 2px 6px; border-radius: 4px; font-weight: 700; }

/* Admin dialog */
.admin-dialog { background: #fff; border-radius: 16px; overflow: hidden; }
.admin-dialog-head { background: #92400e; color: #fff; padding: 16px 20px; font-size: 14px; font-weight: 800; display: flex; align-items: center; gap: 8px; }
.admin-dialog-body { padding: 20px; }
.admin-dialog-foot { border-top: 1px solid #f1f5f9; padding: 12px 20px; display: flex; justify-content: flex-end; gap: 8px; }

/* Misc */
.empty-state { text-align: center; padding: 64px; color: #94a3b8; font-size: 14px; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 10px; margin-top: 16px; }
.page-info { font-size: 12px; color: #64748b; }
</style>